import { IQuestion, IQuiz, IQuizMode, IQuizYear, IUserQuestionAnswer, IUserQuiz } from "utils/types";
import { create } from "zustand";

// export type QuizMode = "timed" | "untimed";
export type QuizState = "idle" | "loading" | "active" | "completed" | "paused" | "reportCard";

export type IGetQuizQuestions = (quizId: string) => Promise<IQuestion[]>;
export type ICompleteQuiz = (userQuiz: Partial<IUserQuiz>, answers: Partial<IUserQuestionAnswer>[]) => Promise<IUserQuiz|undefined>;
interface QuizStore {
  quizYears: IQuizYear[];
  quizDurationMin: number,
  quizTimerCountdown: number;
  quizTimer?:  NodeJS.Timer;

  userId?: string;
  isOpen: boolean;
  quizState: QuizState;
  setQuizState: (quizState: QuizState) => void;
  quizMode: IQuizMode;

  quiz?: IQuizYear;
  questions: IQuestion[];
  questionIndex: number;

  quizAnswers: IUserQuestionAnswer[]
  userQuiz?: IUserQuiz;

  onGetQuizQuestions?: IGetQuizQuestions;
  onComplete?: ICompleteQuiz;

  loadReport: (data: {
    userQuiz: IUserQuiz;
    onGetQuizQuestions: IGetQuizQuestions;
    loadAction: () => Promise<{
      quizYear?: IQuizYear;
      quizYears?: IQuizYear[];
      quizAnswers?: IUserQuestionAnswer[];
    }>
  }) => Promise<void>;
  loadQuiz: (data: {
    quizId: string;
    loadAction: () => Promise<IQuizYear[]>;
    onGetQuizQuestions: IGetQuizQuestions;
  }) => Promise<void>;

  openQuiz: (data: {
    onGetQuizQuestions: IGetQuizQuestions;
    quizYears: IQuizYear[];
  }) => void;
  closeQuiz: () => void;

  startQuiz: (data: {
    quiz: IQuizYear;
    quizMode: IQuizMode;
    quizDuration?: number;
    onComplete: ICompleteQuiz
  }) => Promise<void>;
  pauseQuiz: () => void;
  resumeQuiz: () => void;
  abandonQuiz: () => void;
  resetQuiz: () => void;
  restartQuiz: () => void;


  timeLeft: number;
  score: number;

  nextQuestion: () => void;
  previousQuestion: () => void;
  answerQuestion: (questionId: string, answer: string[]) => void;
  completeQuiz: () => Promise<void>;
}
export const useQuizStore = create<QuizStore>((set, get) => ({
  isOpen: false,
  quizMode: "timed",
  quizState: "idle",
  quizYears: [],
  quiz: undefined,
  quizAnswers: [],
  questionIndex: 0,
  questions: [], 
  timeLeft: 0,
  score: 0,

  quizDurationMin: 60,
  quizTimerCountdown: 60,
  setQuizState(quizState) {
    set({ quizState })
  },
  async loadReport(data) {
    set({ quizState: "loading", isOpen: true })
    const { quizYear, quizAnswers, quizYears } = await data.loadAction();
    set({ 
      quizState: "reportCard",
      quiz: quizYear,
      quizAnswers,
      quizYears,
      userQuiz: data.userQuiz,
      onGetQuizQuestions: data.onGetQuizQuestions, 
    })
  },
  async loadQuiz(data) {
    set({ quizState: "loading", isOpen: true })
    const quizYears = await data.loadAction();
    const quiz = quizYears.find(item => item.quizId === data.quizId);
    set({ 
      quizYears, 
      onGetQuizQuestions: data.onGetQuizQuestions, 
      quizState: "idle",
      quiz,
    })
  },
  openQuiz(data) {
    set({...data, isOpen: true, quizState: "idle"})
  },
  closeQuiz() {
    set({ isOpen: false })
  },
  answerQuestion(questionId, answer) {
    const { quizAnswers } = get();
    const answers = structuredClone(quizAnswers) as IUserQuestionAnswer[]
    const answerIndex = answers.findIndex(item => item.questionId === questionId)
    if(answerIndex !== -1){
      answers[answerIndex].optionIds = answer;
    }else {
      answers.push({ questionId, optionIds: answer } as IUserQuestionAnswer)
    }
    set({ quizAnswers: answers })
  },
  pauseQuiz() {
    const { quizTimer } = get();
    if(quizTimer){
      clearInterval(quizTimer)
    }    
  },
  resetQuiz() {
    const { pauseQuiz } = get();
    pauseQuiz();
    set({ 
      quizState: "idle", 
      quizMode: "untimed", 
      questionIndex: 0,
      questions: [], 
      quiz: undefined,
      quizTimer: undefined,
      quizAnswers: [],
      userQuiz: undefined
    })
  },
  restartQuiz() {
    const { quizDurationMin, pauseQuiz, quizMode } = get();
    pauseQuiz();
    set({ quizTimerCountdown: quizDurationMin, questionIndex: 0 })
    // if(quizMode === "timed"){
    //   const quizTimer = setInterval(() => {
    //     const { quizTimerCountdown, pauseQuiz } = get();
    //     if(quizTimerCountdown > 0 ){
    //       set({ quizTimerCountdown: quizTimerCountdown - 1 })
    //     }else {
    //       pauseQuiz();
    //     }
    //   }, 1000)
    //   set({ quizTimer })
    // }
  },
  async startQuiz(data) {
    const { onGetQuizQuestions, restartQuiz } = get();
    restartQuiz();    
    let { quizDuration, quiz, quizMode, onComplete } = data;
    if(!quizDuration) quizDuration = 60;
    set({ quizState: "loading" })
    const questions = await onGetQuizQuestions?.(quiz.quizId) || []

    set({ 
      quizState: "active", quizMode, quiz,
      quizDurationMin: quizDuration, 
      quizTimerCountdown: quizDuration,
      questions, questionIndex: 0,
      onComplete,
    })
    
    if(quizMode === "timed"){
      const quizTimer = setInterval(() => {
        const { quizTimerCountdown, pauseQuiz } = get();
        if(quizTimerCountdown > 0 ){
          set({ quizTimerCountdown: quizTimerCountdown - 1 })
        }else {
          pauseQuiz();
        }
      }, 1000)
      set({ quizTimer })
    }
  },
  nextQuestion() {
    const { questions, questionIndex } = get();
    if(questions.length - 1 > questionIndex ){
      set({ questionIndex: questionIndex + 1 })
    }
  },
  abandonQuiz() {
    const { resetQuiz } = get();
    resetQuiz();
    set({ isOpen: false })
  },
  resumeQuiz() {
    const { quizMode } = get();
    if(quizMode === "timed") {
      const quizTimer = setInterval(() => {
        const { quizTimerCountdown, pauseQuiz } = get();
        if(quizTimerCountdown > 0 ){
          set({ quizTimerCountdown: quizTimerCountdown - 1 })
        }else {
          pauseQuiz();
        }
      }, 1000)
      set({ quizTimer, quizState: "active" })
    }else {
      set({quizState: "active"})
    }
  },
  async completeQuiz() {
    const {resetQuiz, onComplete, quizAnswers, quiz} = get();
    const userQuiz = await onComplete?.({
      quizId: quiz?.quizId as string
    }, quizAnswers)
    resetQuiz();
    set({ quizState: "completed", userQuiz, quiz , quizAnswers: []})
  },
  previousQuestion() {
    const { questionIndex } = get();
    if(questionIndex !== 0 ){
      set({ questionIndex: questionIndex - 1 })
    }
  },
}));
